import { download, generateCsv, mkConfig } from "export-to-csv"
import { MRT_Row } from "material-react-table"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useMoney } from "../../../../../currency/useMoney"
import {
    MeetingServiceOrder,
    OrderLine,
} from "../../../../../company/reports/accounting/meetingService/meetingServiceTypes"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { MonetaryAmount } from "../../../../../currency/currencyTypes"
import { toCurrency, toDinero } from "../../../../../currency/currencyLib"

export const useExportMeetingService = (chosenStartDate: string, chosenEndDate: string) => {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const { l10n } = useAppLocalization()

    const formattedStartDate = dateTimeFactory.formatDate(dateTimeFactory.fromISO(chosenStartDate))
    const formattedEndDate = dateTimeFactory.formatDate(dateTimeFactory.fromISO(chosenEndDate))

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Meeting-service-${formattedStartDate}_${formattedEndDate}`,
    })

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    const mapMeetingServiceToCsvRow = (order: MeetingServiceOrder, orderLine: OrderLine) => ({
        From: formattedStartDate,
        To: formattedEndDate,
        "Order Id": order.id ?? "",
        "Payment method": order.paymentDetails?.paymentMehod
            ? l10n.getStringForEnum("purchases-payment-method", order.paymentDetails?.paymentMehod ?? "", "")
            : "",
        Type: order.paymentDetails?.type ?? "",
        "Booked date": order.accountingDetails?.bookedTime
            ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.accountingDetails.bookedTime))
            : "",
        "Delivery date": order.deliveryDetails?.time
            ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.deliveryDetails.time))
            : "",
        "Product id": orderLine.productId ?? "",
        "Product name": orderLine.name ?? "",
        Items: orderLine.items ?? "",
        [`Item price (${getCurrencyString(orderLine.itemPrice)})`]: getAmountString(orderLine.itemPrice),
        [`Amount (${getCurrencyString(orderLine.price)})`]: getAmountString(orderLine.price),
        "Order note": order.orderNote ?? "",
        "Delivery type": order.deliveryDetails?.type ?? "",
        "Delivery location": order.deliveryDetails?.location?.name ?? "",
        Sittings: order.deliveryDetails?.sittings ?? "",
        "Meeting name": order.meetingDetails?.meetingName ?? "",
        "Meeting start time": order.meetingDetails?.startDateTime
            ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.meetingDetails.startDateTime))
            : "",
        "Meeting end time": order.meetingDetails?.endDateTime
            ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.meetingDetails.endDateTime))
            : "",
        "Creator name": order.customer?.displayName ?? "",
        "Creator email": order.customer?.email ?? "",
        "Creator user group": order.customer?.userGroup?.name ?? "",
        Company: order.accountingCustomerParty?.name ?? "",
        "Sales location": order.sellerSupplierParty?.name ?? "",
        Channel: order.paymentDetails?.salesChannel ?? "",
        "Debtor ID": order.buyerCustomerParty?.debtorId ?? "",
        "Debtor name": order?.buyerCustomerParty?.name ?? "",
        "Debtor contact": order.buyerCustomerParty?.contactPerson ?? "",
        "Debtor contact email": order.buyerCustomerParty?.email ?? "",
        "Debtor phone": order.buyerCustomerParty?.phoneNumber ?? "",
        "Debtor EAN": order?.buyerCustomerParty?.EAN ?? "",
    })

    const handleExportRows = (rows?: MRT_Row<MeetingServiceOrder>[]) => {
        let exportRows: ReturnType<typeof mapMeetingServiceToCsvRow>[]

        if (rows && rows.length > 0) {
            const rowData = rows.map((row) => row.original)
            exportRows = rowData.flatMap((order) =>
                order.orderLines
                    ? order.orderLines.map((orderLine) => mapMeetingServiceToCsvRow(order, orderLine))
                    : [],
            )
        } else {
            exportRows = [mapMeetingServiceToCsvRow({} as MeetingServiceOrder, {} as OrderLine)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }

    return { handleExportRows }
}
